/**
 * The interactive particle field behind every loading state.
 *
 * A port of the canvas in the legacy loading view (index.html:2415–2530).
 * Particles drift back to a home position, and push away from the pointer
 * within 150px — that repulsion is the whole point of the hint text, so it is
 * preserved exactly, force formula included.
 *
 * What is different is the lifecycle. The legacy version:
 *   - started the loop from an IntersectionObserver it disconnected on first
 *     sight, so a re-shown loader never animated again;
 *   - added a `resize` listener to `window` and never removed it, one per
 *     loader instance;
 *   - stopped the animation only by noticing its own canvas had left the DOM,
 *     which means it kept running for one more frame after every unmount and
 *     forever if the node was merely hidden.
 *
 * Here the loop starts on mount, stops on unmount, and every listener is
 * removed with it. It also stops while the tab is hidden — an animation nobody
 * is watching should not keep a phone's GPU awake.
 */

import { useEffect, useRef } from 'react';

/** Particles per pixel of canvas, and a ceiling so a large screen stays cheap. */
const DENSITY = 1 / 3000;
const MAX_PARTICLES = 420;

/** How close the pointer must be before a particle reacts. */
const REPEL_RADIUS = 150;

/** How fast a displaced particle drifts home. Higher is slower. */
const RETURN_EASING = 15;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function ParticleField({ className = '' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    if (!context) return undefined;

    const host = canvas.parentElement;
    const reduced = prefersReducedMotion();

    let width = 0;
    let height = 0;
    let particles = [];
    let frame = null;
    const pointer = { x: -1000, y: -1000 };

    // Drawing at device resolution keeps the dots crisp on a phone; the context
    // is scaled so the rest of the maths stays in CSS pixels.
    const ratio = Math.min(window.devicePixelRatio || 1, 2);

    const measure = () => {
      width = host?.clientWidth || canvas.clientWidth || 0;
      height = host?.clientHeight || canvas.clientHeight || 0;
      canvas.width = Math.max(1, Math.floor(width * ratio));
      canvas.height = Math.max(1, Math.floor(height * ratio));
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    const makeParticle = () => {
      const x = Math.random() * width;
      const y = Math.random() * height;
      return {
        x,
        y,
        baseX: x,
        baseY: y,
        size: Math.random() * 2.5 + 0.5,
        density: Math.random() * 20 + 1,
        // Teal through blue — the same hue band the legacy field used.
        color: `hsla(${Math.random() * 50 + 160}, 100%, 60%, ${Math.random() * 0.8 + 0.2})`,
      };
    };

    const seed = () => {
      const target = Math.min(MAX_PARTICLES, Math.round(width * height * DENSITY));
      particles = Array.from({ length: Math.max(0, target) }, makeParticle);
    };

    const step = (particle) => {
      const dx = pointer.x - particle.x;
      const dy = pointer.y - particle.y;
      const distance = Math.hypot(dx, dy);

      if (distance < REPEL_RADIUS && distance > 0) {
        const force = (REPEL_RADIUS - distance) / REPEL_RADIUS;
        particle.x -= (dx / distance) * force * particle.density;
        particle.y -= (dy / distance) * force * particle.density;
        return;
      }

      if (particle.x !== particle.baseX) {
        particle.x -= (particle.x - particle.baseX) / RETURN_EASING;
      }
      if (particle.y !== particle.baseY) {
        particle.y -= (particle.y - particle.baseY) / RETURN_EASING;
      }
    };

    const paint = () => {
      context.clearRect(0, 0, width, height);
      for (const particle of particles) {
        context.fillStyle = particle.color;
        context.beginPath();
        context.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        context.fill();
      }
    };

    const animate = () => {
      for (const particle of particles) step(particle);
      paint();
      frame = requestAnimationFrame(animate);
    };

    const start = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(animate);
    };

    const stop = () => {
      if (frame === null) return;
      cancelAnimationFrame(frame);
      frame = null;
    };

    measure();
    seed();

    // Reduced motion still gets the starfield, just not the movement.
    if (reduced) paint();
    else start();

    const onPointerMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
    };

    const onPointerLeave = () => {
      pointer.x = -1000;
      pointer.y = -1000;
    };

    const onVisibility = () => {
      if (reduced) return;
      if (document.hidden) stop();
      else start();
    };

    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerleave', onPointerLeave);
    document.addEventListener('visibilitychange', onVisibility);

    // A panel-sized loader changes size when its siblings load, not only when
    // the window does, so watch the element itself where that is available.
    let observer = null;
    if (host && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        measure();
        seed();
        if (reduced) paint();
      });
      observer.observe(host);
    } else {
      window.addEventListener('resize', measure);
    }

    return () => {
      stop();
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerleave', onPointerLeave);
      document.removeEventListener('visibilitychange', onVisibility);
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`absolute inset-0 h-full w-full cursor-crosshair opacity-70 ${className}`}
    />
  );
}

export default ParticleField;
